import { useState, useMemo, useCallback, useContext, useRef } from "react";
import { WidgetContext } from "../../store/widgetContext";

const CLEAR_COLLECTION = ["articles", "home"];

export const useWidgetLogic = () => {
	const { assistant } = useContext(WidgetContext);

	const [location, setLocation] = useState("home");

	const historyRef = useRef([]);
	const isBackRef = useRef(false);
	const conversationIdRef = useRef(null);
	const assignedAgentsRef = useRef([]);

	const [selectedDocument, setSelectedDocument] = useState(null);
	const [selectedCollection, setSelectedCollection] = useState(null);
	const [conversationId, setConversationId] = useState(null);
	const [assignedAgents, setAssignedAgents] = useState([]);

	const isVoiceChatEnabled = useMemo(
		() =>
			// A voice channel needs a voice: `config.voiceConfig.voiceId` (sub-spec 14) is the one field.
			assistant?.config?.general?.modes?.includes("voice") && !!assistant?.config?.voiceConfig?.voiceId,
		[assistant]
	);
	const isTextChatEnabled = useMemo(
		() => assistant?.config?.general?.modes?.includes("chat"),
		[assistant]
	);

	const handleNavigate = useCallback(
		(data) => {
			const { location: targetLocation, conversationId, assignedAgents, mode } = data || {};
			const isNewChatLocation = targetLocation === "newConversation" || targetLocation === "newVoiceChat";

			if (isNewChatLocation && conversationId === undefined) {
				setConversationId(null);
				setAssignedAgents([]);
				conversationIdRef.current = null;
				assignedAgentsRef.current = [];
			}

			if (conversationId !== undefined) {
				setConversationId(conversationId);
				conversationIdRef.current = conversationId;
			}
			if (assignedAgents !== undefined) {
				setAssignedAgents(assignedAgents);
				assignedAgentsRef.current = assignedAgents;
			}

			if (!targetLocation) return;

			if (!isBackRef.current && mode !== "replace") {
				if (location !== targetLocation) {
					historyRef.current.push({
						location,
						conversationId: conversationIdRef.current,
						assignedAgents: assignedAgentsRef.current,
					});
				}
			} else {
				isBackRef.current = false;
			}

			if (CLEAR_COLLECTION.includes(targetLocation)) setSelectedCollection(null);

			setLocation(targetLocation);
		},
		[location]
	);

	const goBack = useCallback(() => {
		const prev = historyRef.current.pop();
		if (!prev) {
			handleNavigate({ location: "home", mode: "replace" });
			return;
		}

		isBackRef.current = true;
		const prevLocation = prev.location || "home";
		const isChatLocation = prevLocation === "newConversation" || prevLocation === "newVoiceChat";

		handleNavigate({
			location: prevLocation,
			conversationId: isChatLocation ? prev.conversationId : undefined,
			assignedAgents: isChatLocation ? prev.assignedAgents : undefined,
			mode: "replace",
		});
	}, [handleNavigate]);

	const previousLocation =
		historyRef.current.length > 0 ? historyRef.current[historyRef.current.length - 1].location : null;

	return {
		widgetState: {
			selectedDocument,
			selectedCollection,
			location,
			previousLocation,
			isVoiceChatEnabled,
			isTextChatEnabled,
			conversationId,
			assignedAgents,
		},
		handlers: {
			handleNavigate,
			goBack,
			setSelectedDocument,
			setSelectedCollection,
		},
	};
};
